import { getApiProjectsProjectIdAccessRequests } from '@shape-construction/api/src/api';
import {
  getApiProjectsProjectIdAccessRequestsQueryOptions,
  usePostApiProjectsProjectIdAccessRequests,
  usePostApiProjectsProjectIdAccessRequestsProjectAccessRequestIdAccept,
  usePostApiProjectsProjectIdAccessRequestsProjectAccessRequestIdRedirect,
  usePostApiProjectsProjectIdAccessRequestsProjectAccessRequestIdReject,
} from '@shape-construction/api/src/hooks';
import { infiniteQueryOptions, useQueryClient } from '@tanstack/react-query';

// queries
export const getProjectsAccessRequestsInfiniteQueryOptions = (
  ...args: Parameters<typeof getApiProjectsProjectIdAccessRequests>
) => {
  const [projectId, params, options] = args;

  return infiniteQueryOptions({
    queryKey: getApiProjectsProjectIdAccessRequestsQueryOptions(projectId, params).queryKey,
    queryFn: ({ pageParam, signal }) =>
      getApiProjectsProjectIdAccessRequests(projectId, { ...params, ...pageParam }, { ...options, signal }),
    getNextPageParam: ({ meta }) => (meta.currentPage !== meta.totalPages ? { page: meta.currentPage + 1 } : undefined),
    initialPageParam: { page: undefined as undefined | number },
    select: ({ pages }) => ({
      projectAccessRequests: pages.flatMap(({ projectAccessRequests }) => projectAccessRequests),
      totalEntries: pages[0]?.meta.totalEntries ?? 0,
    }),
  });
};

// mutations
export const useCreateProjectAccessRequest: typeof usePostApiProjectsProjectIdAccessRequests = (options) => {
  const queryClient = useQueryClient();

  return usePostApiProjectsProjectIdAccessRequests({
    ...options,
    mutation: {
      ...options?.mutation,
      onSettled: (_, __, variables) => {
        queryClient.invalidateQueries(getApiProjectsProjectIdAccessRequestsQueryOptions(variables.projectId));
      },
    },
  });
};

export const useAcceptProjectAccessRequest: typeof usePostApiProjectsProjectIdAccessRequestsProjectAccessRequestIdAccept =
  (options) => {
    const queryClient = useQueryClient();

    return usePostApiProjectsProjectIdAccessRequestsProjectAccessRequestIdAccept({
      ...options,
      mutation: {
        ...options?.mutation,
        onSettled: (_, __, variables) => {
          queryClient.invalidateQueries(getApiProjectsProjectIdAccessRequestsQueryOptions(variables.projectId));
        },
      },
    });
  };

export const useRejectProjectAccessRequest: typeof usePostApiProjectsProjectIdAccessRequestsProjectAccessRequestIdReject =
  (options) => {
    const queryClient = useQueryClient();

    return usePostApiProjectsProjectIdAccessRequestsProjectAccessRequestIdReject({
      ...options,
      mutation: {
        ...options?.mutation,
        onSettled: (_, __, variables) => {
          queryClient.invalidateQueries(getApiProjectsProjectIdAccessRequestsQueryOptions(variables.projectId));
        },
      },
    });
  };

export const useProjectAccessRequestRedirect = usePostApiProjectsProjectIdAccessRequestsProjectAccessRequestIdRedirect;
